import type { PullRequestState } from '@lixionary-jira/domain';
import { Copy, ExternalLink, GitBranch, GitPullRequest, RefreshCw } from 'lucide-react';
import { useState } from 'react';
import { api, useAction } from '../../api/hooks';
import type { TicketBranch, TicketDetail } from '../../api/ticket-types';
import { Badge, humanize, type Tone } from '../../ui/Badge';
import { Button } from '../../ui/Button';
import { ErrorAlert } from '../../ui/feedback';
import { Card } from '../../ui/misc';
import { Timestamp } from '../../ui/Timestamp';

const BRANCH_STATE: Record<TicketBranch['state'], { label: string; tone: Tone }> = {
  not_created: { label: 'Not created', tone: 'default' },
  pending: { label: 'Creating…', tone: 'info' },
  exists: { label: 'On GitHub', tone: 'success' },
  manual: { label: 'Push manually', tone: 'warning' },
};

export const PR_TONE: Record<PullRequestState, Tone> = {
  open: 'success',
  draft: 'default',
  merged: 'primary',
  closed: 'error',
};

const external = { target: '_blank', rel: 'noopener noreferrer nofollow' } as const;

/** The ticket's git branch (feature/<key> or issues/<key>) and the pull requests opened from it. */
export function CodeCard({ t }: { t: TicketDetail }) {
  const b = t.branch;
  const path = `/projects/${t.project.key}/tickets/${t.number}/branch`;
  const inv = [['ticket', t.project.key, t.number]];
  const create = useAction(() => api.post<TicketBranch>(path), {
    success: (r) =>
      r.state === 'exists'
        ? `Created ${r.name} on GitHub`
        : r.state === 'manual'
          ? `Reserved ${r.name}; push it with the commands shown`
          : `Reserved ${r.name}; Jira will create it on GitHub shortly`,
    invalidate: inv,
  });
  const refresh = useAction(() => api.post<TicketBranch>(`${path}/refresh`), { invalidate: inv });
  const [copied, setCopied] = useState<string | null>(null);
  const copy = (what: string, text: string) =>
    navigator.clipboard.writeText(text).then(
      () => setCopied(what),
      () => undefined,
    );
  const reserved = b.state !== 'not_created';
  const state = BRANCH_STATE[b.state];

  if (!b.repositoryUrl)
    return (
      <Card title="Code" tight>
        <span className="small muted">
          No repository is configured for this project. An admin can set one in project settings.
        </span>
      </Card>
    );

  return (
    <Card
      title="Code"
      tight
      action={
        reserved && (
          <Button
            size="sm"
            variant="ghost"
            icon={<RefreshCw size={14} />}
            loading={refresh.isPending}
            onClick={() => refresh.mutate(undefined)}
            aria-label="Refresh branch and pull requests"
            title="Re-read GitHub now"
          />
        )
      }
    >
      <div className="stack sm">
        <div className="row between nowrap">
          <span className="row nowrap small" style={{ minWidth: 0 }}>
            <GitBranch size={14} aria-hidden />
            {!reserved && <span className="muted">Suggested</span>}
            <span className="mono" style={{ overflow: 'hidden', textOverflow: 'ellipsis' }} title={b.name}>
              {b.name}
            </span>
          </span>
          <Button
            size="sm"
            variant="ghost"
            icon={<Copy size={14} />}
            onClick={() => copy('name', b.name)}
            aria-label="Copy branch name"
            title={copied === 'name' ? 'Copied' : 'Copy branch name'}
          />
        </div>
        <div className="row">
          <Badge tone={state.tone}>{state.label}</Badge>
          {b.baseBranch && <span className="small muted">from {b.baseBranch}</span>}
        </div>
        {b.note && <span className="small muted">{b.note}</span>}

        {t.viewer.canCreateBranch && (b.state === 'not_created' || b.state === 'manual') && (
          <Button
            size="sm"
            variant={b.state === 'not_created' ? 'primary' : 'secondary'}
            icon={<GitBranch size={14} />}
            loading={create.isPending}
            onClick={() => create.mutate(undefined)}
          >
            {b.state === 'not_created' ? 'Create branch' : 'Try again on GitHub'}
          </Button>
        )}
        {create.error && <ErrorAlert error={create.error} />}
        {refresh.error && <ErrorAlert error={refresh.error} />}

        {(b.githubUrl || b.compareUrl) && (
          <div className="row small">
            {b.githubUrl && (
              <a href={b.githubUrl} {...external}>
                <ExternalLink size={12} aria-hidden /> Open on GitHub
              </a>
            )}
            {b.compareUrl && !b.pullRequests.some((p) => p.state === 'open' || p.state === 'draft') && (
              <a href={b.compareUrl} {...external}>
                <GitPullRequest size={12} aria-hidden /> Open a pull request
              </a>
            )}
          </div>
        )}

        {reserved && b.state !== 'exists' && b.gitCommands && (
          <div className="stack sm">
            <div className="row between">
              <span className="small muted">Create it from your checkout</span>
              <Button
                size="sm"
                variant="ghost"
                icon={<Copy size={14} />}
                onClick={() => copy('commands', b.gitCommands!)}
              >
                {copied === 'commands' ? 'Copied' : 'Copy'}
              </Button>
            </div>
            <pre className="code small" style={{ margin: 0 }} aria-label="Git commands">
              {b.gitCommands}
            </pre>
          </div>
        )}

        {reserved && (
          <div className="stack sm">
            <span className="small muted">Pull requests</span>
            {b.pullRequests.length === 0 ? (
              <span className="small muted">
                None yet.
                {b.lastSyncedAt && (
                  <>
                    {' '}
                    Checked <Timestamp value={b.lastSyncedAt} relative />.
                  </>
                )}
              </span>
            ) : (
              <ul className="stack sm" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                {b.pullRequests.map((p) => (
                  <li key={p.number} className="stack" style={{ gap: 2 }}>
                    <a href={p.url} {...external} className="small">
                      #{p.number} {p.title}
                    </a>
                    <span className="row small muted">
                      <Badge tone={PR_TONE[p.state]}>{humanize(p.state)}</Badge>
                      <span>
                        into {p.baseRef}
                        {p.authorLogin ? ` · ${p.authorLogin}` : ''} ·{' '}
                        <Timestamp value={p.mergedAt ?? p.closedAt ?? p.openedAt} relative />
                      </span>
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </Card>
  );
}
